import { useEffect, useMemo, useRef, useState } from 'react'
import { buildComparison, Comparison, CompareFieldKey, CompareRow } from './compare'
import { runVerifyWithOllama } from './ollama'

type VerificationLike = {
  requestId?: string
  browserFileNames?: string[]
} & Record<string, unknown>

export type AutoCheckState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; comparison: Comparison; imageCount: number }
  | { kind: 'error'; message: string }

export type AutoCheck = {
  state: AutoCheckState
  rowByKey: Map<CompareFieldKey, CompareRow>
  appliedKeys: Set<CompareFieldKey>
  markApplied: (key: CompareFieldKey) => void
}

export const useAutoCheck = (verificationRequest: VerificationLike): AutoCheck => {
  const { requestId, browserFileNames } = verificationRequest
  const imageOnlyFileNames = (browserFileNames ?? []).filter(
    (u) => !/\.pdf(\?|#|$)/i.test(u),
  )
  const imagesKey = imageOnlyFileNames.join('|')
  const [state, setState] = useState<AutoCheckState>({ kind: 'idle' })
  const [appliedKeys, setAppliedKeys] = useState<Set<CompareFieldKey>>(new Set())
  const startedForRef = useRef<string | null>(null)

  useEffect(() => {
    if (!requestId) return
    if (imageOnlyFileNames.length === 0) return
    if (startedForRef.current === requestId) return
    startedForRef.current = requestId

    let cancelled = false
    setState({ kind: 'loading' })
    setAppliedKeys(new Set())

    runVerifyWithOllama(verificationRequest, imageOnlyFileNames)
      .then((result) => {
        if (cancelled) return
        const comparison = buildComparison(verificationRequest, result.parsed)
        setState({ kind: 'ready', comparison, imageCount: result.imageCount })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const message = err instanceof Error ? err.message : String(err)
        console.warn('[AutoCheck] failed:', message)
        setState({ kind: 'error', message })
      })

    return () => {
      cancelled = true
    }
  }, [requestId, imagesKey, verificationRequest])

  const rowByKey = useMemo(() => {
    if (state.kind !== 'ready') return new Map<CompareFieldKey, CompareRow>()
    return new Map(state.comparison.rows.map((r) => [r.key, r]))
  }, [state])

  const markApplied = (key: CompareFieldKey) => {
    setAppliedKeys((prev) => {
      const next = new Set(prev)
      next.add(key)
      return next
    })
  }

  return { state, rowByKey, appliedKeys, markApplied }
}
